/**
 * Simplified database operations for AI streaming
 * Direct saves only - no queuing, no complex priority systems
 */

import { supabase } from "@/lib/supabase";
import { isAuthError, handleAuthError } from "@/lib/utils/auth";

/**
 * Clean message data before database save by removing non-schema fields
 * @param {Object} messageData - Raw message data
 * @returns {Object} - Cleaned message data safe for database
 */
function cleanMessageForDatabase(messageData) {
  const cleaned = { ...messageData };

  // Remove fields that don't exist in the database schema
  delete cleaned.toolCalls; // camelCase version doesn't exist in schema
  delete cleaned.isStreaming;
  delete cleaned.isStreamComplete;
  delete cleaned.messageStatus;
  delete cleaned.skipDatabaseSave;
  delete cleaned._dependencies;
  delete cleaned._immediate;
  delete cleaned.threadId; // Use thread_id instead
  delete cleaned.message_index; // UI-only field, not in database schema

  // Ensure required fields are properly set
  cleaned.message_id = cleaned.message_id || cleaned.id;

  // DON'T stringify tool_calls - Supabase JSONB columns handle objects natively
  // The stringification was causing tool_calls to be stored as strings and
  // then parsed incorrectly as character arrays on retrieval

  return cleaned;
}

/**
 * Simple retry wrapper for database operations
 * @param {Function} operation - The operation to retry
 * @param {number} maxRetries - Maximum number of retries (default: 2)
 * @returns {Promise} - Result of the operation
 */
async function retryOperation(operation, maxRetries = 2) {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        // Exponential backoff: 500ms, 1000ms
        await new Promise((resolve) =>
          setTimeout(resolve, 500 * Math.pow(2, attempt - 1))
        );
      }

      return await operation();
    } catch (error) {
      lastError = error;

      // Check for 401 auth errors - don't retry, trigger session expiration immediately
      if (isAuthError(error)) {
        handleAuthError("Database");
        throw error; // Don't retry auth errors
      }

      console.warn(
        `Database operation attempt ${attempt + 1} failed:`,
        error.message
      );
    }
  }

  throw lastError;
}

/**
 * Save user message to database
 * @param {Object} message - User message data
 * @returns {Promise<boolean>} - Success status
 */
export async function saveUserMessage(message) {
  if (!message?.id || !message?.thread_id || message.role !== "user") {
    console.error("Invalid user message for save:", {
      id: message?.id,
      thread_id: message?.thread_id,
      role: message?.role,
      expectedRole: "user",
      content: message?.content?.substring(0, 50),
    });
    return false;
  }

  try {
    return await retryOperation(async () => {
      // Clean the message data before saving
      const cleanedMessage = cleanMessageForDatabase(message);

      const upsertData = {
        id: cleanedMessage.id,
        message_id: cleanedMessage.id,
        thread_id: cleanedMessage.thread_id,
        workspace_id: cleanedMessage.workspace_id,
        role: "user", // CRITICAL: Explicitly set role to "user"
        content: cleanedMessage.content || " ",
        created_at: cleanedMessage.created_at || new Date().toISOString(),
        updated_at: cleanedMessage.updated_at || new Date().toISOString(),
        provider: "user",
        metadata: cleanedMessage.metadata || {},
        attachments: cleanedMessage.attachments || [],
      };

      const { error } = await supabase
        .from("thread_messages")
        .upsert([upsertData], {
          onConflict: "id",
        });

      if (error) throw error;

      return true;
    });
  } catch (error) {
    console.error(`❌ Failed to save user message ${message.id}:`, error);
    return false;
  }
}

/**
 * Save assistant message (without tool calls)
 * @param {Object} message - Assistant message data
 * @returns {Promise<boolean>} - Success status
 */
export async function saveAssistantMessage(message) {
  if (!message?.id || !message?.thread_id || message.role !== "assistant") {
    console.error("Invalid assistant message for save:", message?.id);
    return false;
  }

  // Don't save if it has tool calls - use saveToolCallMessage instead
  if (
    message.tool_calls &&
    Array.isArray(message.tool_calls) &&
    message.tool_calls.length > 0
  ) {
    console.warn(
      "Assistant message has tool calls, use saveToolCallMessage instead"
    );
    return false;
  }

  try {
    return await retryOperation(async () => {
      // Clean the message data before saving
      const cleanedMessage = cleanMessageForDatabase(message);

      const { error } = await supabase.from("thread_messages").upsert(
        [
          {
            id: cleanedMessage.id,
            message_id: cleanedMessage.message_id,
            thread_id: cleanedMessage.thread_id,
            workspace_id: cleanedMessage.workspace_id,
            role: "assistant",
            content: cleanedMessage.content || " ",
            created_at: cleanedMessage.created_at || new Date().toISOString(),
            updated_at: cleanedMessage.updated_at || new Date().toISOString(),
            provider:
              cleanedMessage.provider ||
              cleanedMessage.metadata?.provider ||
              "openai",
            provider_message_id:
              cleanedMessage.provider_message_id || cleanedMessage.id,
            metadata: {
              ...cleanedMessage.metadata,
              model: cleanedMessage.metadata?.model || cleanedMessage.model,
              timestamp: new Date().toISOString(),
            },
          },
        ],
        {
          onConflict: "id",
        }
      );

      if (error) throw error;

      return true;
    });
  } catch (error) {
    console.error(`❌ Failed to save assistant message ${message.id}:`, error);
    return false;
  }
}

/**
 * Save assistant message with tool calls
 * @param {Object} message - Message data with tool calls
 * @returns {Promise<boolean>} - Success status
 */
export async function saveToolCallMessage(message) {
  if (!message?.id || !message?.thread_id || message.role !== "assistant") {
    console.error("Invalid tool call message for save:", message?.id);
    return false;
  }

  if (
    !message.tool_calls ||
    !Array.isArray(message.tool_calls) ||
    message.tool_calls.length === 0
  ) {
    console.warn("No tool calls in message, use saveAssistantMessage instead");
    return false;
  }

  try {
    return await retryOperation(async () => {
      // Clean the message data before saving
      const cleanedMessage = cleanMessageForDatabase(message);

      // tool_calls should be passed as-is (array of objects)
      // Supabase JSONB columns handle objects natively, no stringification needed
      const toolCalls = cleanedMessage.tool_calls;

      const { error } = await supabase.from("thread_messages").upsert(
        [
          {
            id: cleanedMessage.id,
            message_id: cleanedMessage.message_id,
            thread_id: cleanedMessage.thread_id,
            workspace_id: cleanedMessage.workspace_id,
            role: "assistant",
            content: cleanedMessage.content || " ",
            created_at: cleanedMessage.created_at || new Date().toISOString(),
            updated_at: cleanedMessage.updated_at || new Date().toISOString(),
            provider:
              cleanedMessage.provider ||
              cleanedMessage.metadata?.provider ||
              "openai",
            provider_message_id:
              cleanedMessage.provider_message_id || cleanedMessage.id,
            tool_calls: toolCalls, // Pass as native array, not string
            tools_count: message.tool_calls.length,
            metadata: {
              ...cleanedMessage.metadata,
              model: cleanedMessage.metadata?.model || cleanedMessage.model,
              isToolCallMessage: true,
              timestamp: new Date().toISOString(),
            },
          },
        ],
        {
          onConflict: "id",
        }
      );

      if (error) throw error;

      return true;
    });
  } catch (error) {
    console.error(`❌ Failed to save tool call message ${message.id}:`, error);
    return false;
  }
}

/**
 * Save follow-up message after tool execution
 * @param {Object} message - Follow-up message data
 * @param {string} toolCallMessageId - ID of the tool call message this follows
 * @returns {Promise<boolean>} - Success status
 */
export async function saveFollowUpMessage(message, toolCallMessageId) {
  if (!message?.id || !message?.thread_id || message.role !== "assistant") {
    console.error("Invalid follow-up message for save:", message?.id);
    return false;
  }

  if (!toolCallMessageId) {
    console.error("Missing toolCallMessageId for follow-up message");
    return false;
  }

  try {
    return await retryOperation(async () => {
      // Clean the message data before saving
      const cleanedMessage = cleanMessageForDatabase(message);

      const { error } = await supabase.from("thread_messages").upsert(
        [
          {
            id: cleanedMessage.id,
            message_id: cleanedMessage.message_id,
            thread_id: cleanedMessage.thread_id,
            workspace_id: cleanedMessage.workspace_id,
            role: "assistant",
            content: cleanedMessage.content || " ",
            created_at: cleanedMessage.created_at || new Date().toISOString(),
            updated_at: cleanedMessage.updated_at || new Date().toISOString(),
            provider:
              cleanedMessage.provider ||
              cleanedMessage.metadata?.provider ||
              "openai",
            provider_message_id:
              cleanedMessage.provider_message_id || cleanedMessage.id,
            metadata: {
              ...cleanedMessage.metadata,
              model: cleanedMessage.metadata?.model || cleanedMessage.model,
              isFollowUpContent: true,
              toolCallMessageId: toolCallMessageId,
              timestamp: new Date().toISOString(),
            },
          },
        ],
        {
          onConflict: "id",
        }
      );

      if (error) throw error;

      return true;
    });
  } catch (error) {
    console.error(`❌ Failed to save follow-up message ${message.id}:`, error);
    return false;
  }
}

/**
 * Update existing message in database
 * @param {string} messageId - Message ID to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<boolean>} - Success status
 */
export async function updateMessage(messageId, updates) {
  if (!messageId) {
    console.error("Missing messageId for update");
    return false;
  }

  try {
    return await retryOperation(async () => {
      const { error } = await supabase
        .from("thread_messages")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", messageId);

      if (error) throw error;

      return true;
    });
  } catch (error) {
    console.error(`❌ Failed to update message ${messageId}:`, error);
    return false;
  }
}
